<template>
  <g
    class="room-table"
    :transform="`translate(${localX}, ${localY}) rotate(${rotation})`"
    ref="roomTableRef"
  >
    <!--SHAPE SELECTION-->
    <!-- Invisible Interactive Layer -->
    <rect
      v-if="shape === TableShape.SQUARE"
      :x="-width"
      :y="-height"
      :width="width * 2"
      :height="height * 2"
      v-bind="propsInvisibleShape"
      class="square-invisible"
    />
    <!-- Visible Shape -->
    <rect
      v-if="shape === TableShape.SQUARE"
      rx="0.3"
      ry="0.3"
      :x="-width"
      :y="-height"
      :width="width * 2"
      :height="height * 2"
      v-bind="propsVisibleShape"
      class="square"
    />

    <!-- Invisible Interactive Layer -->
    <circle
      v-if="shape === TableShape.CIRCLE"
      :cx="0"
      :cy="0"
      :r="width"
      v-bind="propsInvisibleShape"
      class="circle-invisible"
    />
    <!-- Visible Shape -->
    <circle
      v-if="shape === TableShape.CIRCLE"
      :cx="0"
      :cy="0"
      :r="width"
      v-bind="propsVisibleShape"
      class="circle"
    />

    <!-- Invisible Interactive Layer -->
    <path
      v-if="shape === TableShape.HALF_CIRCLE"
      :d="`M-${width},-${height / 2} a1,1 0 0,0 ${width * 2},0`"
      v-bind="propsInvisibleShape"
      class="half-circle-invisible"
    />
    <!-- Visible Shape -->
    <path
      v-if="shape === TableShape.HALF_CIRCLE"
      :d="`M-${width},-${height / 2} a1,1 0 0,0 ${width * 2},0`"
      v-bind="propsVisibleShape"
      class="half-circle"
    />

    <!-- Invisible Interactive Layer -->
    <polygon
      v-if="shape === TableShape.TRIANGLE"
      :points="trianglePoints"
      v-bind="propsInvisibleShape"
      class="triangle-invisible"
    />
    <!-- Visible Shape -->
    <polygon
      v-if="shape === TableShape.TRIANGLE"
      :points="trianglePoints"
      v-bind="propsVisibleShape"
      class="triangle"
    />

    <PianoShape
      v-if="shape === TableShape.PIANO"
      :width="width"
      :height="height"
      :class="[strokeClass, fillClass, 'piano']"
    />

    <!--LABEL CONTENT SELECTION-->
    <g v-if="marker === 'xmark'" class="xmark">
      <line
        class="line-1 stroke-white"
        :x1="-width + xmarkMargin"
        :y1="-height + xmarkMargin"
        :x2="width - xmarkMargin"
        :y2="height - xmarkMargin"
        :stroke-width="2 / venueScale"
      />
      <line
        class="line-2 stroke-white"
        :x1="-width + xmarkMargin"
        :y1="height - xmarkMargin"
        :x2="width - xmarkMargin"
        :y2="-height + xmarkMargin"
        :stroke-width="2 / venueScale"
      />
    </g>
    <text
      v-else-if="marker === 'checkmark'"
      x="0"
      y="0"
      dominant-baseline="middle"
      text-anchor="middle"
      class="checkmark fill-white"
      style="font-size: 1.3px"
      :transform="`rotate(${-rotation})`"
    >
      &#10003;
    </text>
    <text
      v-else
      x="0"
      y="0"
      dominant-baseline="middle"
      text-anchor="middle"
      :class="[textFillClass, 'label']"
      style="font-size: 1.3px"
      :transform="`rotate(${-rotation})`"
    >
      <slot />
    </text>
  </g>
</template>

<script lang="ts" setup>
import { useDraggable } from '@vueuse/core'
import * as _ from 'lodash-es'
import { storeToRefs } from 'pinia'
import { ComputedRef, SVGAttributes, computed, inject, ref, toRefs, watch } from 'vue'

import PianoShape from '@/common/components/atoms/PianoShape.vue'
import { useSettingStore } from '@/stores/setting'
import { ROOM_TABLE_RELATIVE_WIDTH } from '@/utils/utils'

import { PerformanceTableRead, TableShape, TableShapeType } from '@generated/types'

export type Marker = 'checkmark' | 'xmark' | 'none'
export interface RoomTableProps {
  shape: TableShapeType
  width: number
  height: number
  x: number
  y: number
  rotation?: number
  marker?: Marker
  fillClass?: string
  strokeClass?: string
  textFillClass?: string
  draggable?: boolean
  includeShadow?: boolean
}

const props = withDefaults(defineProps<RoomTableProps>(), {
  rotation: 0,
  marker: 'none',
  fillClass: 'fill-black',
  strokeClass: 'stroke-transparent',
  textFillClass: 'fill-white',
  draggable: false,
  includeShadow: false,
})
const { width, height, shape, draggable, includeShadow, strokeClass, fillClass } = toRefs(props)
const { ratio: aspectRatio } = storeToRefs(useSettingStore())

const trianglePoints = computed<string>(function () {
  return `${-width.value * 1.5},${height.value * 0.5} ${width.value * 0.5},${height.value * 0.5} ${
    width.value * 0.5
  },${-height.value * 1.5} ${-width.value * 1.5},${height.value * 0.5}`
})
const xmarkMargin = computed<number>(function () {
  switch (shape.value) {
    case TableShape.CIRCLE:
      return width.value * 0.4
    case TableShape.HALF_CIRCLE:
      return width.value * 0.7
    default:
      return 0.6
  }
})

// D R A G G I N G    F U N C T I O N A L I T Y
const emit = defineEmits<{
  // eslint-disable-next-line no-unused-vars
  (event: 'update:position', values: Pick<PerformanceTableRead, 'x' | 'y'>): void
}>()
const localX = ref(props.x)
const localY = ref(props.y)
watch([() => props.x, () => props.y], ([x, y]) => {
  localX.value = x
  localY.value = y
})
let mouseToCenterOffset: { xDistance: number; yDistance: number } = { xDistance: 0, yDistance: 0 }
const roomTableRef = ref<SVGGraphicsElement | null>(null)
const svgElementRef = inject('svgElementRef') as ComputedRef<SVGElement | null>
const venueRoomPaddingX = inject('paddingX') as ComputedRef<number>
const venueScale = inject('venueScale') as ComputedRef<number>
useDraggable(roomTableRef, {
  draggingElement: svgElementRef,
  containerElement: svgElementRef,
  onStart(_, { offsetX, offsetY }) {
    if (!draggable.value) return
    const { x, y } = getMouseRelativePosition(offsetX, offsetY)
    mouseToCenterOffset = { xDistance: x - localX.value, yDistance: y - localY.value }
  },
  onMove(_, { offsetX, offsetY }) {
    if (!draggable.value) return
    const { x, y } = getTableRelativePosition(offsetX, offsetY)
    localX.value = x
    localY.value = y
  },
  onEnd(_, { offsetX, offsetY }) {
    if (!draggable.value) return
    const { x, y } = getTableRelativePosition(offsetX, offsetY)
    emit('update:position', { x: Math.round(x), y: Math.round(y) })
  },
})
function getMouseRelativePosition(offsetX: number, offsetY: number): { x: number; y: number } {
  return {
    x: (offsetX - venueRoomPaddingX.value) / venueScale.value,
    y: offsetY / venueScale.value,
  }
}
function getTableRelativePosition(offsetX: number, offsetY: number): { x: number; y: number } {
  const svgRelativeHeight = ROOM_TABLE_RELATIVE_WIDTH / aspectRatio.value
  const { x: relativeMouseX, y: relativeMouseY } = getMouseRelativePosition(offsetX, offsetY)
  return {
    x: _.clamp(relativeMouseX - mouseToCenterOffset.xDistance, 0, ROOM_TABLE_RELATIVE_WIDTH),
    y: _.clamp(relativeMouseY - mouseToCenterOffset.yDistance, 0, svgRelativeHeight),
  }
}

const SHADOW_BLUR = 4
const filterShadow = computed<string>(function () {
  return includeShadow.value
    ? `filter: drop-shadow(0px 0px ${SHADOW_BLUR / venueScale.value}px rgb(0 0 0 / 0.2))`
    : ''
})

const STROKE_WIDTH = 2
const strokeWidth = computed<number>(() => STROKE_WIDTH / venueScale.value)
const propsVisibleShape = computed<
  Pick<SVGAttributes, 'stroke-width' | 'stroke-linejoin' | 'class' | 'style'>
>(() => ({
  'stroke-width': strokeWidth.value,
  'stroke-linejoin': 'round',
  class: [strokeClass.value, fillClass.value],
  style: filterShadow.value,
}))

const STROKE_TARGET_AREA = 16
const propsInvisibleShape = computed<Pick<SVGAttributes, 'stroke-width' | 'stroke' | 'fill'>>(
  () => ({
    'stroke-width': STROKE_TARGET_AREA / venueScale.value,
    stroke: 'transparent',
    fill: 'transparent',
  }),
)
</script>
